import { useState } from "react";
import useShowDialogAlert from "components/DialogAlert/utils/hooks/useDialogAlert";
import { ACTION_TYPES, MESSAGES_VALIDATION_REQUEST_PENALIZE } from "../constants";
import useResourcesUniversitySpacesContext from "views/Recursos/ResourcesUniversitySpaces/utils/context/useResourcesUniversitySpacesContext";
import { 
    requestPenalizeStudent, 
    requestUpdatePenaltyStudent, 
    requestValidatePenalize, 
    requestValidateUpdatePenalty 
} from "services/resources/universitySpaces/aboutUniversitySpaceResources";

const usePenalizeStudents = ({
    space, selectedStudents,
    addActionTypeToListActions,
    dialogs: {
        dialogShowNotActionForHavingAttendanceOrPenalty
    },
    data: {
        reasonsForPenalize
    }, setActionTypeOfNoActionForHaveAttendanceOrPenalty,
}) => {
    const { loadingRequestOfReservationAction } = useResourcesUniversitySpacesContext();
    //#region Custom Hooks
    const dialogPenalizeForm = useShowDialogAlert();
    const dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties = useShowDialogAlert();
    //#endregion
    //#region States
    const [studentsWithRegisteredPenalty, setStudentsWithRegisteredPenalty] = useState([]);
    const [formList, setFormList] = useState([]);
    const [currentPenalty, setCurrentPenalty] = useState(null);
    //#endregion
    //#region Functions
    const validatePenalize = async () => {
        loadingRequestOfReservationAction.show("Validando...");
        const newStudentsWithRegisteredPenalty = await requestValidatePenalize({ 
            reservationDetailIds: selectedStudents.getReservationDetailIds(),
            spaceTypeId: space.spaceTypeId
        });
        loadingRequestOfReservationAction.hide();
        return newStudentsWithRegisteredPenalty;
    }
    function getStudentsNotSavedAndNotHavePenaltiesFromFormList() {
        return formList.filter(({ studentIds }) => selectedStudents.manyStudentsAreSelected(studentIds));
	}
    const selectedStudentsHaveTheSamePenaltiesWhenPenaltyWillBeUpdate = () => {
        const formsFound = getStudentsNotSavedAndNotHavePenaltiesFromFormList();
        if (!formsFound.length) 
            return { 
                continueValidation: false, 
                message: null 
            };
        const haveTheSamePenalties = formsFound.every(({ content: currentContent }) => {
            const { content } = formsFound[0]; 
            return (
                content.penalizeDays === currentContent.penalizeDays &&
                content.reasonForPenalizeId === currentContent.reasonForPenalizeId
            );
        });
        return { 
            stopValidation: true, 
            message: haveTheSamePenalties ? null : MESSAGES_VALIDATION_REQUEST_PENALIZE.UPDATE_PENALTY 
        };
    }
    const isValidUpdatePenalty = async () => {
        // Si solo hay uno seleccionado entonces no hace falta validar
        if (selectedStudents.onlyOneStudentSelected()) 
            return null;
        let validation = selectedStudentsHaveTheSamePenaltiesWhenPenaltyWillBeUpdate();
        if (validation.stopValidation) 
            return validation.message;
        loadingRequestOfReservationAction.show("Validando...");
        const message = await requestValidateUpdatePenalty({ 
            studentPenalizedIds: selectedStudents.getStudentPenaltyIds(),
            spaceTypeId: space.spaceTypeId
        });
        loadingRequestOfReservationAction.hide();
        return message;
    }
    function isForUpdatePenalty() {
        if (selectedStudents.noSelected()) return false;
        return selectedStudents.existStudentsForPenalize();
    }
    function fillFormList(newPenalty) {
        let newFormList = formList
            // Filtrando estudiantes seleccionados de la lista de formularios
            .map(form => {
                // Si ningunos de los estudiantes seleccionados está en el lista de formularios
                if (!selectedStudents.manyStudentsAreSelected(form.studentIds))
                    return form;
                return {
                    ...form,
                    studentIds: form.studentIds.filter(id => !selectedStudents.studentIsSelected(id)),
                    reservationDetailIds: form.reservationDetailIds.filter(id => !selectedStudents.studentWithReservationDetailIdIsSelected(id)),
                    studentPenalizedIds: form.studentPenalizedIds.filter(id => !selectedStudents.studentWithPenalizedIdIsSelected(id))
                };
            })
            // Eliminando formularios sin estudiantes
            .filter(form => form.studentIds.length);
        // Agregando nuevo formulario
        newFormList = [...newFormList, { 
            content: { ...newPenalty },
            studentIds: selectedStudents.getStudentIds(),
            reservationDetailIds: selectedStudents.getReservationDetailIdsWithoutPenalty(), 
            studentPenalizedIds: currentPenalty ? selectedStudents.getStudentPenaltyIds() : []
        }];
        setFormList(newFormList);
    }
    const getInfoTooltipIfExistStudentsInFormList = (reasonsForPenalize, studentId) => {
        const formFound = formList.find(({ studentIds }) => studentIds.includes(studentId));
        if (!formFound) return null;
        const { content } = formFound;
        const reasonForPenalizeFound = reasonsForPenalize.find(({ id }) => content.reasonForPenalizeId === id);
        return {
            reason: reasonForPenalizeFound?.name,
            penalizeDays: content.penalizeDays,
            details: content.details || "-"
        }; 
    }
    const getInfoTooltipOfPenalize = ({ studentId, penalty }) => {
        let result = getInfoTooltipIfExistStudentsInFormList(reasonsForPenalize, studentId);
        if (result) 
            return result;
        const reasonForPenalizeFound = reasonsForPenalize.find(({ id }) => penalty?.reasonForPenalizeId === id);
        result = {
            reason: reasonForPenalizeFound?.name,
            penalizeDays: penalty?.penalizeDays,
            details: penalty?.details || "-"
        };
        return result;
    }
    const openPenalizeStudentDialog = async () => {
        if (isForUpdatePenalty()) {
            // Si es actualización de sanción
            const message = await isValidUpdatePenalty();
            if (message === MESSAGES_VALIDATION_REQUEST_PENALIZE.UPDATE_PENALTY) {
                dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties.open();
                return;
            }
            const firstSelectedStudent = selectedStudents.getFirstSelectedStudent();
            const formFound = formList.find(({ studentIds }) => studentIds.includes(firstSelectedStudent.id));
            setCurrentPenalty(
                formFound 
                ? { ...formFound.content } 
                : { ...firstSelectedStudent.penalty }
            );
        }
        else {
            // Si es creación de sanción
            const newStudentsWithConflicts = await validatePenalize();
            setStudentsWithRegisteredPenalty(newStudentsWithConflicts);
            if (newStudentsWithConflicts.length > 0) {
                dialogShowNotActionForHavingAttendanceOrPenalty.open();
                setActionTypeOfNoActionForHaveAttendanceOrPenalty(ACTION_TYPES.PENALIZE);
                return;
            }
            setCurrentPenalty(null);
        }
        dialogPenalizeForm.open();
    }
    const penalizeStudentFromReservationList = newPenalty => {
        dialogPenalizeForm.hide();
        selectedStudents.markForPenalizeOnlySelectedStudents();
        fillFormList(newPenalty);
        selectedStudents.unselectAllStudents();
        addActionTypeToListActions(ACTION_TYPES.PENALIZE);
    }
    const doRequestPenalizeStudents = async () => {
        for (const form of formList) {
            // Realizando petición de sanción a los estudiantes seleccionados
            const params = form.studentPenalizedIds.length > 0
                ? {
                    loadingRequestMessage: "Editando sanción..." ,
                    request: requestUpdatePenaltyStudent
                } : {
                    loadingRequestMessage: "Sancionando alumno(s)...",
                    request: requestPenalizeStudent
                };
            loadingRequestOfReservationAction.show(params.loadingRequestMessage);
            const { 
                content, reservationDetailIds, studentPenalizedIds 
            } = form;
            const success = await params.request({
                ...content,
                spaceTypeId: space.spaceTypeId,
                reservationDetailIds: reservationDetailIds,
                studentPenalizedIds: studentPenalizedIds
            });
            loadingRequestOfReservationAction.hide();
            if (!success) 
                throw new Error(true);
        }
    }
    //#endregion
    return {
        formList,
        currentPenalty,
        dialogs: {
            dialogPenalizeForm,
            dialogShowNotUpdatePenaltyBecauseStudentsHaveDiferentPenalties
        },
        penalizeStudentFromReservationList,
        fns: {
            openPenalizeStudentDialog,
            doRequestPenalizeStudents,
            isForUpdatePenalty,
            getInfoTooltipOfPenalize
        },
        studentsWithRegisteredPenalty
    };
}	
    
export default usePenalizeStudents;